import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, retry, throwError, timer } from 'rxjs';

const ROOM_TICKET_URL = '/api/socket/room-ticket';
const RETRY_COUNT = 2;
const RETRY_BASE_DELAY_MS = 300;

function isRefusal(error: unknown): boolean {
  const status = (error as HttpErrorResponse)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * A socket room name is a fixed prefix plus an id the client already knows, so the server cannot
 * treat it as a secret. The right to join is proven with a ticket the server mints on an
 * authenticated HTTP request and the client replays in the join message.
 */
@Injectable({ providedIn: 'root' })
export class SocketRoomTicketService {
  private readonly http = inject(HttpClient);

  /**
   * Emits null when the server refused the room or stayed unreachable. A refusal is final and not
   * retried; a network blip or a 5xx is, because losing the ticket loses the whole subscription.
   */
  requestTicket(roomId: string): Observable<string | null> {
    return this.http.post<{ ticket: string }>(ROOM_TICKET_URL, { roomId }).pipe(
      retry({
        count: RETRY_COUNT,
        delay: (error: unknown, attempt: number) =>
          isRefusal(error) ? throwError(() => error) : timer(attempt * RETRY_BASE_DELAY_MS),
      }),
      map(response => response?.ticket ?? null),
      catchError(() => of(null)),
    );
  }
}
